import { DatabaseData, DatabaseInfo, TableInfo, ColumnInfo } from '../types/database'

export class PostgreSQLService {
  private static instance: PostgreSQLService

  private constructor() {}

  static getInstance(): PostgreSQLService {
    if (!PostgreSQLService.instance) {
      PostgreSQLService.instance = new PostgreSQLService()
    }
    return PostgreSQLService.instance
  }

  async parseDatabase(file: File): Promise<DatabaseData> {
    try {
      const content = await file.text()
      return this.parseSQLDump(content, file.name)
    } catch (error) {
      throw new Error(`PostgreSQLファイルの解析に失敗しました: ${error}`)
    }
  }

  private parseSQLDump(content: string, fileName: string): DatabaseData {
    const tables: Record<string, { columns: ColumnInfo[], rows: any[] }> = {}
    const tableInfos: TableInfo[] = []
    
    // SQLダンプからテーブル構造とデータを解析
    const lines = content.split('\n')
    let currentTable: string | null = null
    let currentColumns: ColumnInfo[] = []
    let currentRows: any[] = []
    let inDataSection = false
    
    console.log('=== PostgreSQL Parser Debug ===')
    console.log('Total lines:', lines.length)
    
    for (const line of lines) {
      const trimmedLine = line.trim()
      
      // CREATE TABLE文を検出
      if (trimmedLine.toUpperCase().startsWith('CREATE TABLE')) {
        // 前のテーブルを保存
        if (currentTable && currentColumns.length > 0) {
          tables[currentTable] = { columns: currentColumns, rows: currentRows }
          tableInfos.push({
            name: currentTable,
            columns: currentColumns,
            rowCount: currentRows.length
          })
        }
        
        // 新しいテーブルを開始
        const tableMatch = trimmedLine.match(/CREATE TABLE\s+["`]?(\w+)["`]?\s*\(/i)
        if (tableMatch) {
          currentTable = tableMatch[1]
          currentColumns = []
          currentRows = []
          inDataSection = false
          console.log('Found table:', currentTable)
        }
      }
      
      // 列定義を解析
      else if (currentTable && !inDataSection && (trimmedLine.includes('"') || trimmedLine.includes('`'))) {
        // PostgreSQL形式の列定義を解析
        const columnMatch = trimmedLine.match(/["`](\w+)["`]\s+(\w+)/i)
        if (columnMatch) {
          const columnName = columnMatch[1]
          const columnType = this.normalizeColumnType(columnMatch[2])
          
          currentColumns.push({
            name: columnName,
            type: columnType,
            nullable: !trimmedLine.toUpperCase().includes('NOT NULL'),
            primaryKey: trimmedLine.toUpperCase().includes('PRIMARY KEY')
          })
          console.log('Found column:', columnName, 'type:', columnType, 'line:', trimmedLine)
        } else {
          console.log('No column match for line:', trimmedLine)
        }
      }
      
      // テーブル定義の終了を検出
      else if (currentTable && !inDataSection && trimmedLine === ');') {
        inDataSection = false
        console.log('Table definition ended for:', currentTable)
      }
      
      // INSERT文を検出してデータを解析
      else if (trimmedLine.toUpperCase().startsWith('INSERT INTO')) {
        inDataSection = true
        const insertMatch = trimmedLine.match(/INSERT INTO\s+["`]?(\w+)["`]?\s*VALUES\s*\((.+)\)/i)
        if (insertMatch && currentTable) {
          const values = this.parseInsertValues(insertMatch[2])
          if (values.length === currentColumns.length) {
            const row: any = {}
            currentColumns.forEach((col, index) => {
              row[col.name] = this.parseValue(values[index], col.type)
            })
            currentRows.push(row)
          }
        }
      }
      
             // 複数行のINSERT文を処理
       else if (inDataSection && trimmedLine.startsWith('(') && trimmedLine.endsWith('),')) {
         const values = this.parseInsertValues(trimmedLine.slice(1, -2))
         if (values.length === currentColumns.length) {
           const row: any = {}
           currentColumns.forEach((col, index) => {
             row[col.name] = this.parseValue(values[index], col.type)
           })
           currentRows.push(row)
           console.log('Added row:', row)
         }
       }
      
      // 最後の行のINSERT文を処理（カンマなし）
      else if (inDataSection && trimmedLine.startsWith('(') && trimmedLine.endsWith(');')) {
        const values = this.parseInsertValues(trimmedLine.slice(1, -2))
        if (values.length === currentColumns.length) {
          const row: any = {}
          currentColumns.forEach((col, index) => {
            row[col.name] = this.parseValue(values[index], col.type)
          })
          currentRows.push(row)
        }
      }
    }
    
         // 最後のテーブルを保存
     if (currentTable && currentColumns.length > 0) {
       tables[currentTable] = { columns: currentColumns, rows: currentRows }
       tableInfos.push({
         name: currentTable,
         columns: currentColumns,
         rowCount: currentRows.length
       })
       console.log('Final table:', currentTable, 'columns:', currentColumns.length, 'rows:', currentRows.length)
     }

    const databaseInfo: DatabaseInfo = {
      type: 'postgresql',
      name: fileName.replace(/\.[^/.]+$/, ''),
      size: content.length,
      tables: tableInfos
    }
    
         console.log('=== Parser completed ===')
     console.log('Tables found:', Object.keys(tables))
     console.log('Table infos:', tableInfos)
     
     return {
       info: databaseInfo,
       tables
     }
  }

  private normalizeColumnType(postgresqlType: string): string {
    const typeMap: Record<string, string> = {
      'integer': 'INTEGER',
      'int': 'INTEGER',
      'bigint': 'INTEGER',
      'smallint': 'INTEGER',
      'serial': 'INTEGER',
      'bigserial': 'INTEGER',
      'text': 'TEXT',
      'varchar': 'TEXT',
      'char': 'TEXT',
      'real': 'REAL',
      'double precision': 'REAL',
      'numeric': 'REAL',
      'decimal': 'REAL',
      'boolean': 'BOOLEAN',
      'bool': 'BOOLEAN',
      'date': 'DATETIME',
      'timestamp': 'DATETIME',
      'timestamptz': 'DATETIME',
      'time': 'DATETIME',
      'uuid': 'TEXT',
      'json': 'TEXT',
      'jsonb': 'TEXT'
    }
    
    const normalizedType = postgresqlType.toLowerCase()
    return typeMap[normalizedType] || 'TEXT'
  }

  private parseInsertValues(valuesString: string): string[] {
    const values: string[] = []
    let currentValue = ''
    let inQuotes = false
    let quoteChar = ''
    
    for (let i = 0; i < valuesString.length; i++) {
      const char = valuesString[i]
      
      if (!inQuotes && (char === "'" || char === '"')) {
        inQuotes = true
        quoteChar = char
        continue
      }
      
      if (inQuotes && char === quoteChar) {
        inQuotes = false
        quoteChar = ''
        continue
      }
      
      if (!inQuotes && char === ',') {
        values.push(currentValue.trim())
        currentValue = ''
        continue
      }
      
      currentValue += char
    }
    
    if (currentValue.trim()) {
      values.push(currentValue.trim())
    }
    
    return values
  }

  private parseValue(value: string, columnType: string): any {
    if (value === 'NULL' || value === 'null') {
      return null
    }
    
    // 引用符を除去
    if ((value.startsWith("'") && value.endsWith("'")) || 
        (value.startsWith('"') && value.endsWith('"'))) {
      value = value.slice(1, -1)
    }
    
    switch (columnType) {
      case 'INTEGER':
        const intValue = parseInt(value)
        return isNaN(intValue) ? null : intValue
      case 'REAL':
        const floatValue = parseFloat(value)
        return isNaN(floatValue) ? null : floatValue
      case 'BOOLEAN':
        return value.toLowerCase() === 'true' || value === '1'
      case 'DATETIME':
        return value // ISO形式の日時文字列として保持
      default:
        return value
    }
  }

  async exportDatabase(data: DatabaseData, originalFileName: string): Promise<Blob> {
    let sqlDump = `-- PostgreSQL Database Export\n`
    sqlDump += `-- Generated by Database Viewer\n\n`
    
    // テーブル構造とデータを生成
    for (const [tableName, table] of Object.entries(data.tables)) {
      // CREATE TABLE文
      sqlDump += `CREATE TABLE "${tableName}" (\n`
      const columnDefinitions = table.columns.map(col => {
        let definition = `  "${col.name}" ${this.toPostgreSQLType(col.type)}`
        if (!col.nullable) {
          definition += ' NOT NULL'
        }
        if (col.primaryKey) {
          definition += ' PRIMARY KEY'
        }
        return definition
      })
      sqlDump += columnDefinitions.join(',\n') + '\n);\n\n'
      
      // INSERT文
      if (table.rows.length > 0) {
        sqlDump += `INSERT INTO "${tableName}" (`
        sqlDump += table.columns.map(col => `"${col.name}"`).join(', ')
        sqlDump += ') VALUES\n'
        
        const insertValues = table.rows.map(row => {
          const values = table.columns.map(col => {
            const value = row[col.name]
            if (value === null || value === undefined) {
              return 'NULL'
            }
            if (typeof value === 'string') {
              return `'${value.replace(/'/g, "''")}'`
            }
            if (typeof value === 'boolean') {
              return value ? 'TRUE' : 'FALSE'
            }
            return String(value)
          })
          return `(${values.join(', ')})`
        })
        
        sqlDump += insertValues.join(',\n') + ';\n\n'
      }
    }
    
    return new Blob([sqlDump], { type: 'application/sql' })
  }

  private toPostgreSQLType(type: string): string {
    const typeMap: Record<string, string> = {
      'INTEGER': 'INTEGER',
      'TEXT': 'TEXT',
      'REAL': 'REAL',
      'BOOLEAN': 'BOOLEAN',
      'DATETIME': 'TIMESTAMP'
    }
    return typeMap[type] || 'TEXT'
  }
} 